/* eslint-disable no-dupe-keys */
/* eslint-disable react-hooks/exhaustive-deps */
// =============================================================================
// RouteDataTable
//
// Copy of LocationDataTable with one fix: columns expand to fill the full
// available container width instead of being capped at the sum of minWidths.
// The original LocationDataTable is unchanged.
// =============================================================================
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  FlatList,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  Dimensions,
  useWindowDimensions,
  Platform,
  Animated,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../../constants/fonts";
import LHOverlay from "../../../../components/LHOverlay";
import LhButton from "../../../../components/LhButton";

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------
interface SkeletonRowsProps {
  columns: Column[];
  columnWidths: { [key: string]: { minWidth: number; flex: number } };
  rowCount?: number;
}

const RouteSkeletonRows: React.FC<SkeletonRowsProps> = React.memo(
  ({ columns, columnWidths, rowCount = 10 }) => {
    const pulse = React.useRef(new Animated.Value(0.35)).current;

    React.useEffect(() => {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulse, {
            toValue: 0.85,
            duration: 750,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(pulse, {
            toValue: 0.35,
            duration: 750,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
      loop.start();
      return () => loop.stop();
    }, [pulse]);

    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIdx) => (
          <View key={rowIdx} style={skeletonStyles.row}>
            {columns.map((col, colIdx) => {
              const cw = columnWidths[col.columnKey];
              const minWidth = cw ? cw.minWidth : (col.width ?? 120);
              const flex = cw ? cw.flex : 0;
              const barPct = 45 + ((rowIdx * 11 + colIdx * 17) % 40);
              return (
                <View key={col.columnKey} style={[skeletonStyles.cell, { minWidth, flex }]}>
                  <Animated.View
                    style={[skeletonStyles.bar, { width: `${barPct}%`, opacity: pulse }]}
                  />
                </View>
              );
            })}
          </View>
        ))}
      </>
    );
  }
);

const skeletonStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    height: 48,
    minHeight: 48,
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    width: '100%',
    alignItems: 'center',
  },
  cell: {
    height: 48,
    paddingHorizontal: 16,
    paddingVertical: 14,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  bar: {
    height: 12,
    borderRadius: 6,
    backgroundColor: '#C8D6EA',
  },
});

type ColumnType = "toggle" | "checkbox";

type Column = {
  columnKey: string;
  header: string;
  type?: ColumnType;
  renderCell?: (item: any) => React.ReactNode;
  formatter?: (value: any) => string;
  flexGrow?: number;
  width?: number;
  getSortValue?: (item: any) => any;
  filterOptions?: { label: string; value: string }[];
  sortable?: boolean;
};

type Props = {
  data: any[];
  columns: Column[];
  tableHeader: string;
  tableDescription: string;
  maxRecordsToShow?: number;
  onRowClick?: (rowData: any) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (sortKey: string, direction: 'asc' | 'desc') => void;
  externalPage?: number;
  externalTotalPages?: number;
  onExternalPageChange?: (page: number) => void;
  columnFlexValues?: { [columnKey: string]: number };
  showHeaderTopBorder?: boolean;
  expansionRenderer?: (item: any) => React.ReactNode;
  isLoading?: boolean;
  /** Pass API-provided isFirst flag to disable first/prev navigation */
  isFirstPage?: boolean;
  /** Pass API-provided isLast flag to disable next/last navigation */
  isLastPage?: boolean;
  /** Active column filter values: { [columnKey]: string[] } */
  columnFilters?: Record<string, string[]>;
  /** Called when a column filter changes */
  onColumnFilter?: (columnKey: string, values: string[]) => void;
};

// Memoized header component
interface TableHeaderRowProps {
  columns: Column[];
  sortConfig: { key: string; direction: 'asc' | 'desc' } | null;
  columnWidths: { [key: string]: { minWidth: number; flex: number } };
  showHeaderTopBorder: boolean;
  isIOSMobile: boolean;
  onSort: (columnKey: string) => void;
  columnFilters: Record<string, string[]>;
  activeFilterKey: string | null;
  onOpenFilter: (columnKey: string, options: { label: string; value: string }[], btnRef: any) => void;
}

const RouteTableHeaderRow = React.memo(
  ({
    columns,
    sortConfig,
    columnWidths,
    showHeaderTopBorder,
    isIOSMobile,
    onSort,
    columnFilters,
    activeFilterKey,
    onOpenFilter,
  }: TableHeaderRowProps) => {
    const filterBtnRefs = React.useRef<Record<string, any>>({});
    return (
      <View
        style={[
          styles.headerRow,
          showHeaderTopBorder
            ? { borderTopWidth: 2, borderTopColor: '#D7E5F8' }
            : { borderTopWidth: 0, borderTopColor: 'transparent' },
        ]}
      >
        {columns.map((col) => {
          const isSortable = col.sortable !== false && col.columnKey !== '_actions';
          const isSorted = sortConfig?.key === col.columnKey;
          const hasFilter = !!col.filterOptions?.length;
          const isFiltered = (columnFilters[col.columnKey]?.length ?? 0) > 0;
          const isFilterOpen = activeFilterKey === col.columnKey;
          return (
            <View
              key={col.columnKey}
              style={[
                styles.cellContainer,
                {
                  justifyContent: isIOSMobile ? 'center' : 'flex-start',
                  flexDirection: 'row',
                  alignItems: 'center',
                },
                columnWidths[col.columnKey] && {
                  minWidth: columnWidths[col.columnKey].minWidth,
                  flex: columnWidths[col.columnKey].flex,
                  ...(Platform.OS !== 'web' && {
                    width: columnWidths[col.columnKey].minWidth,
                  }),
                },
              ]}
            >
              {/* Sort area */}
              <TouchableOpacity
                onPress={() => isSortable && onSort(col.columnKey)}
                style={{ flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0 }}
                activeOpacity={isSortable ? 0.7 : 1}
              >
                <View style={styles.headerCellContent}>
                  <Text style={styles.headerCellText} numberOfLines={1}>{col.header}</Text>
                  {isSortable && (
                    <View style={styles.sortIconContainer}>
                      {!isSorted ? (
                        <MaterialIcons name="unfold-more" size={16} color="#616161" style={styles.sortIcon} />
                      ) : sortConfig?.direction === 'asc' ? (
                        <MaterialIcons name="keyboard-arrow-up" size={16} color="#616161" style={styles.sortIcon} />
                      ) : (
                        <MaterialIcons name="keyboard-arrow-down" size={16} color="#616161" style={styles.sortIcon} />
                      )}
                    </View>
                  )}
                </View>
              </TouchableOpacity>
              {/* Filter icon – only for columns with filterOptions */}
              {hasFilter && (
                <TouchableOpacity
                  ref={(r) => { filterBtnRefs.current[col.columnKey] = r; }}
                  onPress={() =>
                    onOpenFilter(col.columnKey, col.filterOptions!, filterBtnRefs.current[col.columnKey])
                  }
                  style={[styles.filterIconBtn, isFilterOpen && styles.filterIconBtnActive]}
                  activeOpacity={0.6}
                  hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
                >
                  <MaterialIcons
                    name="filter-alt"
                    size={15}
                    color={isFiltered || isFilterOpen ? '#005CBC' : '#9E9E9E'}
                  />
                </TouchableOpacity>
              )}
            </View>
          );
        })}
      </View>
    );
  }
);

const RouteDataTable = ({
  data,
  columns,
  maxRecordsToShow = 10,
  onRowClick,
  onPageSizeChange,
  onSort,
  externalPage,
  externalTotalPages,
  onExternalPageChange,
  columnFlexValues,
  showHeaderTopBorder = true,
  expansionRenderer,
  isLoading = false,
  isFirstPage,
  isLastPage,
  columnFilters = {},
  onColumnFilter,
}: Props) => {
  const isExternalPagination = externalPage !== undefined && externalTotalPages !== undefined;
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayContent, setOverlayContent] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(maxRecordsToShow);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [columnWidths, setColumnWidths] = useState<{
    [key: string]: { minWidth: number; flex: number };
  }>({});
  const [layoutCalculated, setLayoutCalculated] = useState(false);
  // ── FIX: track actual container width so columns expand to fill full row ──
  const [containerWidth, setContainerWidth] = useState(0);

  // ── Column filter dropdown ────────────────────────────────────────────────
  const [filterModal, setFilterModal] = useState<{
    columnKey: string;
    options: { label: string; value: string }[];
    top: number;
    left: number;
  } | null>(null);
  // Use a ref so handleOpenFilter stays stable (no filterModal in deps)
  const filterModalRef = useRef(filterModal);
  useEffect(() => { filterModalRef.current = filterModal; }, [filterModal]);

  const handleOpenFilter = useCallback((
    columnKey: string,
    options: { label: string; value: string }[],
    btnRef: any,
  ) => {
    if (filterModalRef.current?.columnKey === columnKey) {
      setFilterModal(null);
      return;
    }
    if (btnRef) {
      btnRef.measure((_x: number, _y: number, w: number, h: number, pageX: number, pageY: number) => {
        const screenWidth = Dimensions.get('window').width;
        const dropdownMinWidth = 164;
        const edgeMargin = 8;
        // If dropdown would overflow the right edge, right-align it to the button's right side
        const rawLeft = pageX;
        const left =
          rawLeft + dropdownMinWidth + edgeMargin > screenWidth
            ? Math.max(edgeMargin, pageX + w - dropdownMinWidth)
            : rawLeft;
        setFilterModal({ columnKey, options, top: pageY + h + 4, left });
      });
    }
  }, []);

  const { width: screenWidth } = useWindowDimensions();
  const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;

  const estimateTextWidth = (text: string, isBold: boolean = false) => {
    const baseWidth = Platform.OS === "web" ? 8 : 6;
    const boldMultiplier = isBold ? 1.2 : 1;
    let charCount = 0;
    const str = String(text);
    for (let i = 0; i < str.length; i++) {
      if (/[WM]/.test(str[i])) {
        charCount += 1.3;
      } else if (/[ilt1|]/.test(str[i])) {
        charCount += 0.6;
      } else {
        charCount += 1;
      }
    }
    return charCount * baseWidth * boldMultiplier;
  };

  const calculateColumnWidths = useMemo(() => {
    const widths: { [key: string]: { minWidth: number; flex: number } } = {};
    const padding = 24;
    const minColumnWidth = 120;
    const maxColumnWidth = 300;
    const safeData = Array.isArray(data) ? data : [];

    columns.forEach((col) => {
      let maxWidth = estimateTextWidth(col.header, true) + padding;

      safeData.forEach((item) => {
        let cellValue = item[col.columnKey];
        if (col.formatter && cellValue !== undefined && cellValue !== null) {
          cellValue = col.formatter(cellValue);
        }
        if (cellValue !== undefined && cellValue !== null) {
          const cellWidth = estimateTextWidth(String(cellValue)) + padding;
          maxWidth = Math.max(maxWidth, cellWidth);
        }
      });

      if (col.width !== undefined) {
        maxWidth = col.width;
      } else {
        maxWidth = Math.max(minColumnWidth, Math.min(maxWidth, maxColumnWidth));
      }

      let flex = 1;
      if (col.width !== undefined) {
        flex = 0;
      } else if (columnFlexValues?.[col.columnKey]) {
        flex = columnFlexValues[col.columnKey];
      } else if (col.flexGrow !== undefined) {
        flex = col.flexGrow;
      } else if (maxWidth > 200) {
        flex = 2;
      } else if (maxWidth > 150) {
        flex = 1.5;
      }

      widths[col.columnKey] = { minWidth: maxWidth, flex };
    });

    return widths;
  }, [data, columns, screenWidth, columnFlexValues]);

  useEffect(() => {
    setColumnWidths(calculateColumnWidths);
  }, [calculateColumnWidths]);

  const sortedData = useMemo(() => {
    if (isExternalPagination) return data;
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      const column = columns.find((col) => col.columnKey === sortConfig.key);
      if (!column) return 0;
      const aValue = column.getSortValue ? column.getSortValue(a) : a[column.columnKey];
      const bValue = column.getSortValue ? column.getSortValue(b) : b[column.columnKey];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columns, isExternalPagination]);

  const totalPages = isExternalPagination
    ? (externalTotalPages as number)
    : Math.ceil(sortedData.length / rowsPerPage);
  const activePage = isExternalPagination ? (externalPage as number) : currentPage;
  const paginatedData = isExternalPagination
    ? sortedData
    : sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Sum of column minWidths (used as minimum width for horizontal scroll)
  const totalTableWidth = useMemo(() => {
    return columns.reduce((sum, col) => {
      const w = columnWidths[col.columnKey];
      return sum + (w ? w.minWidth : (col.width ?? 120));
    }, 0);
  }, [columns, columnWidths]);

  // For external (API) pagination, pages are 0-indexed; display as 1-based
  const displayPage = isExternalPagination ? activePage + 1 : activePage;
  // Disabled states driven by API isFirst/isLast flags when provided
  const atFirst = isFirstPage !== undefined
    ? isFirstPage
    : (isExternalPagination ? activePage === 0 : activePage === 1);
  const atLast = isLastPage !== undefined
    ? isLastPage
    : (isExternalPagination ? activePage >= totalPages - 1 : activePage === totalPages);
  // First/last page indices differ by pagination mode
  const firstPageIndex = isExternalPagination ? 0 : 1;
  const lastPageIndex = isExternalPagination ? totalPages - 1 : totalPages;

  // ── FIX: expand inner view to fill the container when it's wider than content
  const effectiveWidth = containerWidth > 0
    ? Math.max(totalTableWidth, containerWidth)
    : totalTableWidth;

  const handleSort = useCallback((columnKey: string) => {
    let newDirection: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === columnKey) {
      newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
      setSortConfig({ key: columnKey, direction: newDirection });
    } else {
      setSortConfig({ key: columnKey, direction: "asc" });
    }
    onSort?.(columnKey, newDirection);
  }, [sortConfig, onSort]);

  const showOverlayWithCustomContent = (content: any) => {
    setOverlayContent(content);
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setOverlayContent(null);
  };

  const getStatusStyle = (status: string) => {
    const normalizedStatus = status
      ?.toString()
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");

    switch (normalizedStatus) {
      case "in progress":
        return {
          container: {
            backgroundColor: "#E5A408", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
      case "completed":
      case "complete":
        return {
          container: {
            backgroundColor: "#027C06", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
      case "active":
        return {
          container: {
            backgroundColor: "#005CBC", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
      default:
        return {
          container: {
            backgroundColor: "#E5A408", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
    }
  };

  const renderRow = ({ item, index }: { item: any; index: number }) => {
    const isSelected = selectedRowIndex === index;

    const handleCellClick = (col: Column, cellValue: any) => {
      if (col.columnKey === "type") {
        if (onRowClick) onRowClick({ ...item, isPulseCheckNavigation: true });
        return;
      }
      if (col.columnKey === "Title" || col.columnKey === "Shift") {
        if (onRowClick) onRowClick({ ...item, isShiftHandOverNavigation: true, columnKey: col.columnKey });
        return;
      }
      setSelectedRowIndex(index);
      if (onRowClick) {
        onRowClick({ ...item, columnKey: col.columnKey });
      } else {
        showOverlayWithCustomContent(item);
      }
    };

    const rowView = (
      <View
        style={[styles.row, isSelected && styles.selectedRow]}
        testID="table-row"
        key={`row-${index}`}
      >
        {columns.map((col) => (
          <View
            key={col.columnKey}
            style={[
              styles.cellContainer,
              { justifyContent: "flex-start" },
              columnWidths[col.columnKey] && {
                minWidth: columnWidths[col.columnKey].minWidth,
                flex: columnWidths[col.columnKey].flex,
                ...(Platform.OS !== "web" && { width: columnWidths[col.columnKey].minWidth }),
                height: Platform.OS === "ios" ? 90 : col.header === "Zone" ? "auto" : 48,
              },
            ]}
          >
            {col.renderCell ? (
              <React.Fragment key={`custom-cell-${col.columnKey}-${index}`}>
                {col.renderCell(item)}
              </React.Fragment>
            ) : col.columnKey === "action" && item[col.columnKey] === "Resume" ? (
              <LhButton
                key={`action-button-${index}`}
                size="small"
                text={item[col.columnKey]}
                style={{ width: 100 }}
                onPress={() => console.log(`Action clicked for row ${index}`)}
              />
            ) : (
              <TouchableOpacity
                key={`cell-${col.columnKey}-${index}`}
                onPress={() => handleCellClick(col, item[col.columnKey])}
                style={[styles.cellTouchable, { alignItems: "flex-start", width: "100%" }]}
              >
                {(() => {
                  const colKeyLower = String(col.columnKey ?? "").toLowerCase();
                  const headerLower = String(col.header ?? "").toLowerCase();
                  const isStatusColumn =
                    colKeyLower === "status" || colKeyLower === "action" || headerLower === "status";
                  const cellValue = item[col.columnKey];

                  if (isStatusColumn) {
                    const statusStyle = getStatusStyle(cellValue);
                    return (
                      <View style={statusStyle.container}>
                        <Text style={statusStyle.text}>{cellValue}</Text>
                      </View>
                    );
                  }

                  return (
                    <Text
                      style={[
                        styles.cell,
                        colKeyLower === "id" || headerLower === "location id"
                          ? styles.firstCellText
                          : styles.otherCellText,
                        colKeyLower === "type" ? styles.typeCell : {},
                      ]}
                      numberOfLines={2}
                      ellipsizeMode="tail"
                    >
                      {col.formatter && cellValue !== undefined && cellValue !== null
                        ? col.formatter(cellValue)
                        : cellValue ?? "—"}
                    </Text>
                  );
                })()}
              </TouchableOpacity>
            )}
          </View>
        ))}
      </View>
    );

    const expansionNode = expansionRenderer ? expansionRenderer(item) : null;
    return (
      <React.Fragment key={`row-frag-${index}`}>
        {rowView}
        {expansionNode}
      </React.Fragment>
    );
  };

  const rowsPerPageOptions = [10, 25, 50, 100];

  const goToPage = (p: number) => {
    if (isExternalPagination) {
      onExternalPageChange?.(p);
    } else {
      setCurrentPage(p);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    onPageSizeChange?.(newRowsPerPage);
  };

  const renderMobilePagination = () => (
    <View style={styles.mobilePaginationContainer}>
      <View style={styles.mobilePaginationBox}>
        <TouchableOpacity
          disabled={atFirst}
          onPress={() => goToPage(activePage - 1)}
          style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell, atFirst && styles.mobilePaginationCellDisabled]}
          testID="mobile-prev-page-button"
          accessibilityLabel="Previous page"
        >
          <MaterialIcons name="chevron-left" size={16} color={atFirst ? "#A0A0A0" : "#005CBC"} />
        </TouchableOpacity>
        <View style={[styles.mobilePaginationCell, styles.mobilePaginationCurrentCell]}>
          <Text style={styles.mobilePaginationCurrentText}>{displayPage}</Text>
        </View>
        <View style={[styles.mobilePaginationCell, styles.mobilePaginationNextCell]}>
          <Text style={styles.mobilePaginationNextText}>of {totalPages}</Text>
        </View>
        <TouchableOpacity
          disabled={atLast}
          onPress={() => goToPage(activePage + 1)}
          style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell, atLast && styles.mobilePaginationCellDisabled]}
          testID="mobile-next-page-button"
          accessibilityLabel="Next page"
        >
          <MaterialIcons name="chevron-right" size={16} color={atLast ? "#A0A0A0" : "#005CBC"} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderDesktopPagination = () => (
    <View style={styles.paginationContainer}>
      <View style={styles.rowsPerPageContainer}>
        <Text style={styles.rowsPerPageLabel}>Rows per page</Text>
        <View style={{ position: "relative" }}>
          <TouchableOpacity
            style={styles.dropdownContainer}
            onPress={() => setDropdownVisible(!dropdownVisible)}
            testID="rows-per-page-dropdown"
            accessibilityLabel="Rows per page selector"
          >
            <Text style={styles.dropdownValue}>{rowsPerPage}</Text>
            <MaterialIcons name={dropdownVisible ? "keyboard-arrow-up" : "keyboard-arrow-down"} size={20} color="#616161" />
          </TouchableOpacity>
          {dropdownVisible && (
            <View style={styles.dropdownOptionsContainer}>
              {rowsPerPageOptions.map((option) => (
                <TouchableOpacity
                  key={option}
                  style={[styles.dropdownOption, option === rowsPerPage && styles.dropdownOptionSelected]}
                  onPress={() => { handleRowsPerPageChange(option); setDropdownVisible(false); }}
                  testID={`rows-per-page-option-${option}`}
                  accessibilityLabel={`${option} rows per page`}
                >
                  <Text style={[styles.dropdownOptionText, option === rowsPerPage && styles.dropdownOptionTextSelected]}>
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      </View>

      <View style={styles.pageNavigationContainer}>
        <TouchableOpacity disabled={atFirst} onPress={() => goToPage(firstPageIndex)} style={styles.navigationButtonNoBorder} testID="first-page-button" accessibilityLabel="First page">
          <MaterialIcons name="first-page" size={20} color={atFirst ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
        <TouchableOpacity disabled={atFirst} onPress={() => goToPage(activePage - 1)} style={styles.navigationButtonNoBorder} testID="prev-page-button" accessibilityLabel="Previous page">
          <MaterialIcons name="keyboard-arrow-left" size={20} color={atFirst ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
        <View style={styles.pageIndicatorContainer}>
          <Text style={styles.pageText}>Page</Text>
          <View style={styles.currentPageBox}>
            <Text style={styles.currentPageText}>{displayPage}</Text>
          </View>
          <Text style={styles.pageOfText}>of <Text style={styles.totalPagesText}>{totalPages}</Text></Text>
        </View>
        <TouchableOpacity disabled={atLast} onPress={() => goToPage(activePage + 1)} style={styles.navigationButtonNoBorder} testID="next-page-button" accessibilityLabel="Next page">
          <MaterialIcons name="keyboard-arrow-right" size={20} color={atLast ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
        <TouchableOpacity disabled={atLast} onPress={() => goToPage(lastPageIndex)} style={styles.navigationButtonNoBorder} testID="last-page-button" accessibilityLabel="Last page">
          <MaterialIcons name="last-page" size={20} color={atLast ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderPaginationControls = () => {
    const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;
    if (isIOSMobile) return renderMobilePagination();
    return renderDesktopPagination();
  };

  return (
    <TouchableWithoutFeedback onPress={() => { setDropdownVisible(false); setFilterModal(null); }}>
      {/* onLayout captures the real rendered width of the table container */}
      <View
        style={styles.container}
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      >
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          style={{ flexGrow: 1 }}
          contentContainerStyle={{ minWidth: effectiveWidth }}
          scrollEventThrottle={16}
          nestedScrollEnabled={true}
        >
          {/* effectiveWidth = max(totalTableWidth, containerWidth) so flex fills the row */}
          <View style={{ width: effectiveWidth, minWidth: effectiveWidth }}>
            <RouteTableHeaderRow
              columns={columns}
              sortConfig={sortConfig}
              columnWidths={columnWidths}
              showHeaderTopBorder={showHeaderTopBorder}
              isIOSMobile={isIOSMobile}
              onSort={handleSort}
              columnFilters={columnFilters}
              activeFilterKey={filterModal?.columnKey ?? null}
              onOpenFilter={handleOpenFilter}
            />
            {isLoading ? (
              <RouteSkeletonRows columns={columns} columnWidths={columnWidths} rowCount={10} />
            ) : paginatedData.length > 0 ? (
              <View
                style={
                  Platform.OS === "ios" || Platform.OS === "android"
                    ? { flex: 1, maxHeight: 800, minHeight: 500 }
                    : { flex: 1, minHeight: 500 }
                }
              >
                <FlatList
                  data={paginatedData}
                  renderItem={renderRow}
                  keyExtractor={(_, index) => index.toString()}
                  scrollEnabled={false}
                  testID="route-table-list"
                />
              </View>
            ) : (
              <View style={styles.noDataContainer}>
                <Text style={styles.noDataText}>No data available</Text>
              </View>
            )}
          </View>
        </ScrollView>
        {!isLoading && sortedData.length > 0 && renderPaginationControls()}

        {/* Column filter dropdown – rendered in Modal to bypass ScrollView overflow clipping */}
        <Modal
          visible={!!filterModal}
          transparent
          animationType="none"
          onRequestClose={() => setFilterModal(null)}
        >
          <TouchableWithoutFeedback onPress={() => setFilterModal(null)}>
            <View style={{ flex: 1 }}>
              {filterModal && (
                <View
                  style={[
                    styles.filterDropdownPanel,
                    { position: 'absolute', top: filterModal.top, left: filterModal.left },
                  ]}
                >
                  {/* "All" – clears this column's filter */}
                  <TouchableOpacity
                    style={[
                      styles.filterOption,
                      !(columnFilters[filterModal.columnKey]?.length) && styles.filterOptionSelected,
                    ]}
                    onPress={() => onColumnFilter?.(filterModal.columnKey, [])}
                  >
                    <View
                      style={[
                        styles.filterCheckbox,
                        !(columnFilters[filterModal.columnKey]?.length) && styles.filterCheckboxChecked,
                      ]}
                    >
                      {!(columnFilters[filterModal.columnKey]?.length) && (
                        <MaterialIcons name="check" size={10} color="#FFFFFF" />
                      )}
                    </View>
                    <Text
                      style={[
                        styles.filterOptionText,
                        !(columnFilters[filterModal.columnKey]?.length) && styles.filterOptionTextActive,
                      ]}
                    >
                      All
                    </Text>
                  </TouchableOpacity>
                  <View style={styles.filterDivider} />
                  {filterModal.options.map((opt) => {
                    const selected = columnFilters[filterModal.columnKey]?.includes(opt.value) ?? false;
                    return (
                      <TouchableOpacity
                        key={opt.value}
                        style={[styles.filterOption, selected && styles.filterOptionSelected]}
                        onPress={() => {
                          const curr = columnFilters[filterModal.columnKey] ?? [];
                          const next = selected
                            ? curr.filter((v) => v !== opt.value)
                            : [...curr, opt.value];
                          onColumnFilter?.(filterModal.columnKey, next);
                        }}
                      >
                        <View style={[styles.filterCheckbox, selected && styles.filterCheckboxChecked]}>
                          {selected && <MaterialIcons name="check" size={10} color="#FFFFFF" />}
                        </View>
                        <Text style={[styles.filterOptionText, selected && styles.filterOptionTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          </TouchableWithoutFeedback>
        </Modal>

        <LHOverlay
          visible={overlayVisible}
          onDismiss={closeOverlay}
          title={overlayContent ? `${overlayContent.type} Details` : "Row Details"}
          size="large"
        >
          {overlayContent && (
            <View style={styles.overlayContent}>
              {/* Overlay content rendering */}
            </View>
          )}
        </LHOverlay>
      </View>
    </TouchableWithoutFeedback>
  );
};

export default RouteDataTable;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  toggleContainer: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    minHeight: 40,
  },
  cellContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    flex: 0,
    overflow: "hidden",
    minHeight: 48,
    height: 48,
  },
  centerContent: {
    alignItems: "center",
    justifyContent: "center",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    height: 48,
    minHeight: 48,
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    width: "100%",
    zIndex: 1,
  },
  row: {
    flexDirection: "row",
    paddingVertical: 0,
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    width: "100%",
    flex: 1,
    minHeight: 48,
    height: 48,
    alignItems: "center",
  },
  selectedRow: {
    backgroundColor: "#e0f7fa",
  },
  cellTouchable: {
    flex: 1,
    justifyContent: "center",
    width: "100%",
    overflow: "hidden",
    paddingHorizontal: 4,
    paddingVertical: 0,
  },
  cell: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    flexShrink: 1,
    width: "100%",
    flexWrap: "wrap",
    flexDirection: "row",
    textAlignVertical: "center",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  headerCellText: {
    fontWeight: "700",
    fontSize: 12,
    color: "#616161",
    textTransform: "uppercase",
    lineHeight: 18,
    includeFontPadding: false,
    padding: 0,
    width: "auto",
    flexShrink: 0,
    textAlign: "left",
    fontFamily: FontFamily.bold,
  },
  firstCellText: {
    color: "#005CBC",
    fontWeight: "500",
    fontFamily: FontFamily.regular,
  },
  otherCellText: {
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  typeCell: {
    color: "#FF6720",
    fontWeight: "bold",
    fontFamily: FontFamily.bold,
  },
  overlayContent: {
    padding: Platform.OS === "web" ? 16 : 12,
  },
  noDataContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#d7e5f8",
  },
  noDataText: {
    color: "#616161",
    fontSize: 16,
    fontFamily: FontFamily.regular,
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
  },
  rowsPerPageContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  rowsPerPageLabel: {
    fontSize: 14,
    color: "#616161",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  dropdownContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    minWidth: 60,
    justifyContent: "space-between",
  },
  dropdownValue: {
    fontSize: 14,
    color: "#3A3A3A",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  pageNavigationContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
    flexShrink: 1,
  },
  navigationButtonNoBorder: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  pageIndicatorContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  currentPageBox: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#005CBC",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  },
  currentPageText: {
    fontSize: 14,
    color: "#2D2D2D",
    fontWeight: "600",
    fontFamily: FontFamily.regular,
  },
  pageText: {
    fontSize: 14,
    color: "#2D2D2D",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  pageOfText: {
    fontSize: 14,
    color: "#616161",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  totalPagesText: {
    fontSize: 14,
    color: "#616161",
    fontWeight: "700",
    fontFamily: FontFamily.bold,
  },
  dropdownOptionsContainer: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    marginBottom: 6,
    zIndex: 1000,
  },
  dropdownOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  dropdownOptionSelected: {
    backgroundColor: "#F5F9FF",
  },
  dropdownOptionText: {
    fontSize: 14,
    color: "#3A3A3A",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  dropdownOptionTextSelected: {
    color: "#005CBC",
    fontWeight: "600",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  // ── Filter icon + dropdown ────────────────────────────────────────────────
  filterIconBtn: {
    paddingHorizontal: 3,
    paddingVertical: 2,
    marginLeft: 2,
    borderRadius: 3,
  },
  filterIconBtnActive: {
    backgroundColor: '#EBF3FF',
  },
  filterDropdownPanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    minWidth: 164,
    paddingVertical: 4,
    ...Platform.select({
      web: { boxShadow: '0 4px 16px rgba(0,94,192,0.15)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 9,
    gap: 8,
  },
  filterOptionSelected: {
    backgroundColor: '#EBF3FF',
  },
  filterOptionText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#3A3A3A',
  },
  filterOptionTextActive: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  filterCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: '#9E9E9E',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterCheckboxChecked: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  filterDivider: {
    height: 1,
    backgroundColor: '#E8EEF8',
    marginVertical: 2,
  },
  headerCellContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    gap: 12,
  },
  sortIconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  sortIcon: {
    alignSelf: "center",
  },
});
